/* 133 §4 — HOW THE CALENDAR DRAWS, RESOLVED ONCE.
 *
 * Laurent asked for these three in Settings, three times over 132 §3 and §7,
 * and once more on the strips' contrast: "tweakable in Settings since this is a
 * major interface part."
 *
 * It is the twin of `resolveScheduleAxis` and it exists for the same reason:
 * four surfaces read the answer (the two 14-day strips, the agenda's list and
 * its calendar), and one resolution is what stops them disagreeing about what
 * the firm asked for. `FirmSettings.calendarDisplay` is a SIBLING column of
 * `scheduleAxis` and `holidayCountries` — see the migration for why.
 *
 * EVERY DEFAULT REPRODUCES EXACTLY WHAT 133 §2 AND 132 §7 RULE, which is §4's
 * own requirement: a firm that never opens the panel sees no change from this
 * section at all. Shading ON in both places, the alternating band OFF — it
 * stays gone.
 *
 * PER-KEY FALLBACK, not per-object. A stored `{ agendaAltBand: true }` must
 * keep the other two at their defaults rather than turning them off, so a firm
 * that answered one question has not silently answered three. That also makes
 * a fourth switch additive: an old row simply defaults on the key it has never
 * heard of.
 */

export type CalendarDisplay = {
  /** 133 §2 — a non-working day or observed holiday takes `--nwd` on the
   *  14-day strips (dashboard + both matter lists). */
  stripNonWorking: boolean;
  /** 132 §7(b) — the same field in the agenda's list and calendar views. */
  agendaNonWorking: boolean;
  /** 132 §7(a) — the alternating day band, which 132 removed. OFF by default:
   *  "it stays gone". This is what puts it back within a firm's reach without
   *  putting it back on anybody's screen. */
  agendaAltBand: boolean;
};

export const CALENDAR_DISPLAY_DEFAULTS: CalendarDisplay = {
  stripNonWorking: true,
  agendaNonWorking: true,
  agendaAltBand: false,
};

/** A stored value is honoured only when it is a real boolean. `undefined`,
 *  `null` and anything a hand-edited row might hold fall back to the default —
 *  the same posture `resolveScheduleAxis` takes with a malformed axis. */
const flag = (v: unknown, dflt: boolean): boolean => (typeof v === "boolean" ? v : dflt);

export function resolveCalendarDisplay(
  firmSettings: { calendarDisplay?: unknown } | null | undefined,
): CalendarDisplay {
  const raw = firmSettings?.calendarDisplay;
  const o = raw && typeof raw === "object" && !Array.isArray(raw) ? (raw as Record<string, unknown>) : {};
  return {
    stripNonWorking: flag(o.stripNonWorking, CALENDAR_DISPLAY_DEFAULTS.stripNonWorking),
    agendaNonWorking: flag(o.agendaNonWorking, CALENDAR_DISPLAY_DEFAULTS.agendaNonWorking),
    agendaAltBand: flag(o.agendaAltBand, CALENDAR_DISPLAY_DEFAULTS.agendaAltBand),
  };
}
